import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';

const MarkdownP = ({ children }: { children?: React.ReactNode }) => (
  <p style={{ margin: '0.5em 0' }}>{children}</p>
);
MarkdownP.displayName = 'MarkdownP';

const MarkdownH1 = ({ children }: { children?: React.ReactNode }) => (
  <h1
    style={{
      marginTop: '1em',
      marginBottom: '0.5em',
      fontSize: '1.5em',
    }}
  >
    {children}
  </h1>
);
MarkdownH1.displayName = 'MarkdownH1';

const MarkdownH2 = ({ children }: { children?: React.ReactNode }) => (
  <h2
    style={{
      marginTop: '1em',
      marginBottom: '0.5em',
      fontSize: '1.3em',
    }}
  >
    {children}
  </h2>
);
MarkdownH2.displayName = 'MarkdownH2';

const MarkdownH3 = ({ children }: { children?: React.ReactNode }) => (
  <h3
    style={{
      marginTop: '1em',
      marginBottom: '0.5em',
      fontSize: '1.1em',
    }}
  >
    {children}
  </h3>
);
MarkdownH3.displayName = 'MarkdownH3';

const MarkdownH4 = ({ children }: { children?: React.ReactNode }) => (
  <h4 style={{ marginTop: '1em', marginBottom: '0.5em' }}>{children}</h4>
);
MarkdownH4.displayName = 'MarkdownH4';

const MarkdownUl = ({ children }: { children?: React.ReactNode }) => (
  <ul style={{ margin: '0.5em 0', paddingLeft: '1.5em' }}>{children}</ul>
);
MarkdownUl.displayName = 'MarkdownUl';

const MarkdownOl = ({ children }: { children?: React.ReactNode }) => (
  <ol style={{ margin: '0.5em 0', paddingLeft: '1.5em' }}>{children}</ol>
);
MarkdownOl.displayName = 'MarkdownOl';

const MarkdownLi = ({ children }: { children?: React.ReactNode }) => (
  <li style={{ margin: '0.25em 0' }}>{children}</li>
);
MarkdownLi.displayName = 'MarkdownLi';

const MarkdownCode = (props: { children?: React.ReactNode; className?: string }) => {
  const { children, ...rest } = props;
  const inline = !rest.className;
  return inline ? (
    <code
      style={{
        backgroundColor: '#f5f5f5',
        padding: '0.2em 0.4em',
        borderRadius: '3px',
        fontFamily: 'monospace',
      }}
    >
      {children}
    </code>
  ) : (
    <pre
      style={{
        backgroundColor: '#f5f5f5',
        padding: '1em',
        borderRadius: '5px',
        overflow: 'auto',
      }}
    >
      <code style={{ fontFamily: 'monospace' }}>{children}</code>
    </pre>
  );
};
MarkdownCode.displayName = 'MarkdownCode';

const MarkdownBlockquote = ({ children }: { children?: React.ReactNode }) => (
  <blockquote
    style={{
      borderLeft: '4px solid #ddd',
      paddingLeft: '1em',
      margin: '1em 0',
      color: '#666',
    }}
  >
    {children}
  </blockquote>
);
MarkdownBlockquote.displayName = 'MarkdownBlockquote';

const MarkdownA = ({ href, children }: { href?: string; children?: React.ReactNode }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    style={{ color: '#1976d2', textDecoration: 'underline' }}
  >
    {children}
  </a>
);
MarkdownA.displayName = 'MarkdownA';

export const ReportViewer = ({ reportText = '' }) => {
  // Check if content contains markdown syntax
  const hasMarkdown = /[*_`#[\]]/g.test(reportText);

  if (hasMarkdown) {
    // Render as markdown
    return (
      <ReactMarkdown
        remarkPlugins={[remarkBreaks]}
        components={{
          p: MarkdownP,
          h1: MarkdownH1,
          h2: MarkdownH2,
          h3: MarkdownH3,
          h4: MarkdownH4,
          ul: MarkdownUl,
          ol: MarkdownOl,
          li: MarkdownLi,
          code: MarkdownCode,
          blockquote: MarkdownBlockquote,
          a: MarkdownA,
        }}
      >
        {reportText}
      </ReactMarkdown>
    );
  }

  // Fallback to structured report rendering
  const lines = reportText.split('\n').filter((line) => line.trim() !== '');

  const renderLine = (line: string, index: number) => {
    const trimmed = line.trim();

    // Títulos numerados ex: "1. Identificação"
    if (/^\d+\.\s/.test(trimmed)) {
      return (
        <h4 key={index} style={{ marginTop: '1em', marginBottom: '0.5em' }}>
          {trimmed}
        </h4>
      );
    }

    // Bullet principal ex: "• Unidade da Federação..."
    if (trimmed.startsWith('•')) {
      return (
        <p key={index} style={{ margin: 0, paddingLeft: '1em' }}>
          {trimmed}
        </p>
      );
    }

    // Sub-bullet ex: "– Agricultura familiar..."
    if (trimmed.startsWith('–')) {
      return (
        <p key={index} style={{ margin: 0, paddingLeft: '2em' }}>
          {trimmed}
        </p>
      );
    }

    // Linha simples (sem bullet ou título)
    return (
      <p key={index} style={{ margin: 0 }}>
        {trimmed}
      </p>
    );
  };

  return <div>{lines.map(renderLine)}</div>;
};
